import { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  buildCardVerificationDetails,
  CardTokenizationError,
  getPaymentClientConfig,
  loadSquareSdk,
  type CardVerification,
  type SquareCard,
  type SquareTokenError,
  type SquareTokenResult,
} from '@/api/payments';

/**
 * Tokenize the entered card (DNKADM-0487). The caller says what the card is for — the
 * intent, the buyer and, for a charge, the amount — and Square runs the bank's buyer
 * verification (3-D Secure) before it hands back the token. A cancelled or failed
 * verification rejects with a `CardTokenizationError` whose `verification` is `true`.
 */
export type TokenizeFn = (verification: CardVerification) => Promise<string>;

interface CardPaymentFieldProps {
  tenantSlug: string;
  locationRef: string;
  onReady: (tokenize: TokenizeFn) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

type FieldStatus = 'loading' | 'ready' | 'unconfigured' | 'unsupported' | 'error';

function CardPaymentField({
  tenantSlug,
  locationRef,
  onReady,
  onError,
  disabled = false,
}: CardPaymentFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const [status, setStatus] = useState<FieldStatus>('loading');

  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    let cancelled = false;
    let card: SquareCard | null = null;

    async function initCard() {
      setStatus('loading');
      try {
        const config = await getPaymentClientConfig(tenantSlug, locationRef);
        if (cancelled) return;
        if (!config.configured) {
          setStatus('unconfigured');
          return;
        }
        if (config.provider !== 'square') {
          setStatus('unsupported');
          return;
        }

        const applicationId = config.clientConfig['applicationId'];
        const squareLocationId = config.clientConfig['squareLocationId'];
        if (!applicationId || !squareLocationId) {
          throw new Error('SQUARE_CLIENT_CONFIG_INCOMPLETE');
        }

        const sdk = await loadSquareSdk(config.environment);
        if (cancelled) return;
        const payments = sdk.payments(applicationId, squareLocationId);
        card = await payments.card();
        if (cancelled || !containerRef.current) {
          await card.destroy?.();
          return;
        }

        await card.attach(containerRef.current);
        if (cancelled) {
          await card.destroy?.();
          return;
        }

        const mountedCard = card;
        setStatus('ready');
        onReadyRef.current(async (verification) => tokenizeCard(mountedCard, verification));
      } catch (error) {
        if (cancelled) return;
        setStatus('error');
        onErrorRef.current?.(error instanceof Error ? error : new Error('CARD_FIELD_INIT_FAILED'));
      }
    }

    void initCard();

    return () => {
      cancelled = true;
      void card?.destroy?.();
    };
  }, [tenantSlug, locationRef]);

  return (
    <Stack spacing={1}>
      {status === 'loading' && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading secure card field...</Typography>
        </Stack>
      )}

      {status === 'unconfigured' && (
        <Alert severity="info">Card payments are not configured for this location.</Alert>
      )}

      {status === 'unsupported' && (
        <Alert severity="info">This payment provider is not supported for card entry.</Alert>
      )}

      {status === 'error' && (
        <Alert severity="error">The secure card field could not be loaded.</Alert>
      )}

      <Box
        ref={containerRef}
        aria-disabled={disabled || undefined}
        data-testid="square-card-container"
        sx={{
          display: status === 'loading' || status === 'ready' ? 'block' : 'none',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          minHeight: status === 'ready' ? 46 : 0,
          border: status === 'ready' ? '1px solid' : 'none',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          p: status === 'ready' ? 1.25 : 0,
        }}
      />
    </Stack>
  );
}

/**
 * DNKADM-0487: tokenize WITH `verificationDetails`, so Square runs buyer verification first.
 *
 * How the SDK (v1) reports a verification that did not pass, as read from the loaded
 * bundle: when the bank's challenge fails or is cancelled, `tokenize()` THROWS a
 * `TokenizationError` whose `errors` carry `{ type: 'VERIFICATION_ERROR' }`. A `Cancel` /
 * `Abort` result status (the SDK's "cancelled by the user" outcome) and a thrown
 * `VerifyBuyerError` are treated the same way. Field problems come back as an `Invalid`
 * result with per-field errors and stay validation errors.
 */
async function tokenizeCard(card: SquareCard, verification: CardVerification): Promise<string> {
  let result: SquareTokenResult;
  try {
    result = await card.tokenize(buildCardVerificationDetails(verification));
  } catch (error) {
    if (isVerificationFailure(error)) {
      throw new CardTokenizationError(thrownDetail(error), { verification: true });
    }
    throw error;
  }
  if (result.status === 'OK' && result.token) return result.token;
  if (isValidationTokenizationResult(result)) {
    throw new CardTokenizationError(tokenizationDetail(result), { validation: true });
  }
  throw new CardTokenizationError(tokenizationDetail(result), {
    verification: isVerificationTokenizationResult(result),
  });
}

function tokenizationDetail(result: SquareTokenResult): string {
  return result.errors?.[0]?.message ?? result.errors?.[0]?.detail ?? result.status;
}

/** A field the card form owns (card number, expiry, CVV, postal code) — not our own input. */
function isCardFieldError(error: SquareTokenError): boolean {
  return typeof error.field === 'string' && !error.field.startsWith('verificationDetails');
}

function isValidationTokenizationResult(result: SquareTokenResult): boolean {
  const errors = result.errors ?? [];
  if (errors.some(isCardFieldError)) return true;
  if (errors.some(isVerificationError)) return false;
  if (result.status.toUpperCase() === 'INVALID') {
    // An `Invalid` that only names our own `verificationDetails` is a programming error, not
    // something the person at the desk can fix by finishing the card.
    return !errors.some((error) => error.field?.startsWith('verificationDetails') === true);
  }
  return errors.some((error) => {
    const code = `${error.code ?? ''} ${error.category ?? ''}`.toUpperCase();
    return /CARD|CVV|CVC|EXPIR|POSTAL|VALID/.test(code);
  });
}

const VERIFICATION_ERROR_TYPE = 'VERIFICATION_ERROR';

function isVerificationError(error: SquareTokenError): boolean {
  return (error.type ?? '').toUpperCase() === VERIFICATION_ERROR_TYPE;
}

function isVerificationTokenizationResult(result: SquareTokenResult): boolean {
  const status = result.status.toUpperCase();
  if (status === 'CANCEL' || status === 'ABORT') return true;
  return result.errors?.some(isVerificationError) ?? false;
}

function isVerificationFailure(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const named = error as { name?: unknown; errors?: unknown };
  if (named.name === 'VerifyBuyerError') return true;
  if (!Array.isArray(named.errors)) return false;
  return named.errors.some(
    (entry: unknown) =>
      typeof entry === 'object' && entry !== null && isVerificationError(entry as SquareTokenError),
  );
}

function thrownDetail(error: unknown): string {
  return error instanceof Error ? error.message : 'VERIFICATION_FAILED';
}

export default CardPaymentField;
